/**
 * How a task's dates read — `docs/activity.md` §8.1, §8.5 (Boards 9, 16 C,
 * 19 A): *Tue, Oct 20*, *Today · 2:00 PM*, *2 days late*, *on Tom’s
 * calendar*. The day is the company's (the zone `me` carries) and today is
 * the server's (`useActivity().today`), so a list and the Board cannot
 * disagree about which day a task is late.
 */
import { addDays, daysLateWords, weekStartOf, zonedLocal } from '@rowted/shared'
import type { ListRow } from '../../lib/use-list.js'
import type { DueTone } from './types.js'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const utc = (day: string): Date => new Date(`${day}T12:00:00Z`)

/** *Tue, Oct 20*. */
export function dayLabel(day: string): string {
  const date = utc(day)
  return `${DAYS[date.getUTCDay()]}, ${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`
}

/** *Tue*. */
export const weekdayShort = (day: string): string => DAYS[utc(day).getUTCDay()]!

/** *Oct 13*. */
export function monthDay(day: string): string {
  const date = utc(day)
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`
}

/** *2:00 PM*. */
export function clockWords(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

/** Today, tomorrow and next Monday — §8.5's quick due dates. */
export function quickDays(today: string): { today: string; tomorrow: string; nextWeek: string } {
  return { today, tomorrow: addDays(today, 1), nextWeek: addDays(weekStartOf(today), 8) }
}

export interface DueReading {
  /** *Today · 2:00 PM*, *Tue, Oct 20*. */
  words: string
  tone: DueTone
  /** Whether it has a time (and so sits on somebody's calendar). */
  timed: boolean
  /** *2 days late*, or *on Tom’s calendar*. */
  sub: string | null
  subTone: 'late' | 'muted'
}

const str = (value: unknown): string => (value === null || value === undefined ? '' : String(value))

/**
 * A Tasks row's Due cell (§8.1): the date, red with *<n> days late* under it
 * when late, amber when today, a calendar glyph and *on <first name>’s
 * calendar* when it has a time. A task that is done reads in plain ink.
 */
export function rowDue(row: ListRow, timeZone: string, today: string, done: boolean): DueReading | null {
  const at = str(row.dueAt)
  if (!at) return null
  const local = zonedLocal(new Date(at), timeZone)
  const timed = row.onCalendar === true
  const day = local.date === today ? 'Today' : dayLabel(local.date)
  const words = timed ? `${day} · ${clockWords(local.minutes)}` : day
  const late = !done && (row.late === true || (local.date < today))
  const tone: DueTone = done ? 'other' : late ? 'late' : local.date === today ? 'today' : 'other'
  const firsts = str(row.assignees).split(', ').filter(Boolean)
  let sub: string | null = null
  let subTone: DueReading['subTone'] = 'muted'
  if (late) {
    const days = Number(row.daysLate ?? 0) || daysBetween(local.date, today)
    sub = daysLateWords(days)
    subTone = 'late'
  } else if (timed && firsts.length > 0) {
    sub = firsts.length === 1 ? `on ${firsts[0]}’s calendar` : 'on their calendars'
  }
  return { words, tone, timed, sub, subTone }
}

function daysBetween(from: string, to: string): number {
  return Math.round((utc(to).getTime() - utc(from).getTime()) / 86_400_000)
}

/**
 * When a task was finished, on a done card (Board 19 B): *Wed* within the
 * last week, *Oct 13* before that, *Today* today.
 */
export function doneWhen(at: string, timeZone: string, today: string): string {
  const day = zonedLocal(new Date(at), timeZone).date
  if (day === today) return 'Today'
  if (day > addDays(today, -7)) return weekdayShort(day)
  return monthDay(day)
}

/** *also on Dana Okafor and J-3319* — the contact and the job, else the first two (Board 9 C). */
export function alsoOnWords(links: readonly { type: string; label: string }[]): string | null {
  if (links.length === 0) return null
  const contact = links.find((link) => link.type === 'contact')
  const job = links.find((link) => link.type === 'job')
  const two = contact && job ? [contact, job] : links.slice(0, 2)
  return `also on ${two.map((link) => link.label).join(' and ')}`
}
